import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@skyeng/libs/shared/store';
import { DsCheckboxSize } from '../../checkbox.interface';
import { dsUpdateComponentCssVars } from '@skyeng/libs/shared/ui-kit-v2/theme';
import { CHECKBOX_CSS_VARS_PREFIX, CHECKBOX_CUSTOMISABLE_PARAMS } from '../../checkbox.const';
import { DsSizeDirective } from '@skyeng/libs/shared/ui-kit-v2/base';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { DsBreakpointService } from '@skyeng/libs/shared/ui-kit-v2/layout';

const DEFAULT_SIZE = 'm';

@DecorateUntilDestroy()
@Component({
  selector: 'ds-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DsCheckboxComponent), multi: true }],
  standalone: false,
})
export class DsCheckboxComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() checked: boolean;
  @Input() disabled: boolean;
  @Input() indeterminate: boolean;
  @Input() name?: string;
  @Input() size: DsCheckboxSize = 'm';

  @Output() checkedChange = new EventEmitter<boolean>();

  @ViewChild('root', { static: true }) rootRef: ElementRef<HTMLElement>;

  private store = new Store({
    checked: false,
    disabled: false,
    size: undefined as DsCheckboxSize | undefined,
    inputSize: undefined as DsCheckboxSize | undefined,
  });
  private isMobile = this.dsBreakpointService.isSmall();

  data$ = this.store.select();

  constructor(
    private renderer2: Renderer2,
    @Optional() private dsSizeDirective: DsSizeDirective,
    private dsBreakpointService: DsBreakpointService,
  ) {}

  ngOnInit(): void {
    dsUpdateComponentCssVars({
      renderer: this.renderer2,
      rootElement: this.rootRef,
      componentPrefix: CHECKBOX_CSS_VARS_PREFIX,
      params: CHECKBOX_CUSTOMISABLE_PARAMS,
      type: 'default',
    });
    this.initSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.checked) {
      this.writeValue(this.checked);
    }
    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }
    if (changes.size) {
      this.store.updateFields({ inputSize: this.size }, { queue: true });
    }
  }

  ngOnDestroy(): void {}

  writeValue(checked: unknown): void {
    this.store.updateFields({ checked: !!checked });
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.store.updateFields({ disabled: isDisabled });
  }

  touched(): void {
    this.onTouched();
  }

  changed(): void {
    const checked = !this.store.get('checked');
    this.store.updateFields({ checked });
    this.onChange(checked);
    this.checkedChange.emit(checked);
  }

  private onChange(_: boolean): void {}

  private onTouched(): void {}

  private initSize(): void {
    const inheritedSize$ = this.dsSizeDirective ? this.dsSizeDirective.observe() : of(null);
    combineLatest([this.store.select('inputSize'), inheritedSize$])
      .pipe(
        map(([inputSize, inheritSize]) => inputSize ?? inheritSize ?? DEFAULT_SIZE),
        map(size => (this.isMobile ? 's' : size)),
        takeUntilDestroyed(this),
      )
      .subscribe(size => {
        this.store.updateFields({ size }, { queue: true });
      });
  }
}
